import React, { useMemo, useReducer } from "react"

import { RecordElement } from "shared/types/RecordElement"

import { PayloadAction } from "@reduxjs/toolkit"

import {
  closeAllOverlaysHandler,
  closeOverlayByIDHandler,
  closeOverlayByTypeHandler,
  openOverlayHandler
} from "./overlayActionHandlers"
import { OverlayReducerState } from "./types"

const defaultState: OverlayReducerState = {
  overlays: []
}

export const overlayActionHandlers = {
  openOverlay: openOverlayHandler,
  closeOverlay: closeOverlayByTypeHandler,
  closeOverlayByID: closeOverlayByIDHandler,
  closeAllOverlays: closeAllOverlaysHandler
}

export type ActionTypes<S, T> = {
  [K in keyof T]: T[K] extends (state: S, action: infer A) => unknown
    ? K extends string
      ? A extends PayloadAction<infer P>
        ? PayloadAction<P, K>
        : { type: K }
      : never
    : never
}

export type OverlayAction = RecordElement<
  ActionTypes<OverlayReducerState, typeof overlayActionHandlers>
>

export type DispatchMappedActions<S, T> = {
  [K in keyof T]: T[K] extends (state: S, action: infer A) => S
    ? A extends PayloadAction<infer P>
      ? (action: P) => void
      : () => void
    : never
}

export interface OverlayContextType {
  state: OverlayReducerState
  dispatch: (action: OverlayAction) => void
}

export const OverlayContext = React.createContext<OverlayContextType>({
  state: defaultState,
  dispatch: () => {}
})

function overlayReducer(state: OverlayReducerState, action: OverlayAction) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return overlayActionHandlers[action.type](state, action as any)
}

export function OverlayContextProvider({
  children,
  initialState
}: {
  children?: React.ReactNode
  initialState?: OverlayReducerState
}) {
  const [state, dispatch] = useReducer(
    overlayReducer,
    initialState || defaultState
  )

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch])

  return (
    <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>
  )
}
